// Copyright (c) 2026.  .
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import * as React from 'react';
import { useRef, useState } from 'react';
import { RemoteTrackPublication, Track, VideoQuality } from 'livekit-client';
import { useMediaTrackBySourceOrName } from './hooks/use-media-track-by-source-or-name';
import type { ParticipantClickEvent, TrackReference } from '@livekit/components-core';
import { useEnsureTrackRef } from '@livekit/components-react';
import * as useHooks from 'usehooks-ts';
import {
  Pause,
  PictureInPicture,
  Play,
  Settings,
  Volume2,
  VolumeX,
  ZoomInIcon,
  ZoomOutIcon,
} from 'lucide-react';
import '@/styles/call.css';
import { MdFullscreen, MdFullscreenExit } from 'react-icons/md';
import { CyberKoalaCallsIcon } from '@/components/icons/cyberkoala-calls-icon.tsx';
import { isPardusBrowser } from '@/components/check-user-agent.tsx';
import { cn } from '@/lib/utils.ts';
import { useTheme } from '@/hooks/use-theme.ts'; // 👈 ДОБАВЛЕНО

/** @public */
export interface VideoTrackProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  trackRef?: TrackReference;
  onTrackClick?: (evt: ParticipantClickEvent) => void;
  onSubscriptionStatusChanged?: (subscribed: boolean) => void;
  manageSubscription?: boolean;
  focus?: boolean;
  showControls?: boolean;
  onMuteParticipant?: () => void;
  screenShareAudioLevel?: number;
  volume?: number;
  localSettingsNode?: React.ReactNode;
  onVolumeChange?: (newVol: number) => void;
  volumePercent?: number;
  volumeColor?: string;
}

export const VideoTrack: (
  props: VideoTrackProps & React.RefAttributes<HTMLVideoElement>
) => React.ReactNode = /* @__PURE__ */ React.forwardRef<HTMLVideoElement, VideoTrackProps>(
  function VideoTrack(
    {
      onTrackClick,
      onClick,
      onSubscriptionStatusChanged,
      trackRef,
      manageSubscription,
      focus,
      showControls = true,
      onMuteParticipant,
      screenShareAudioLevel = 0,
      volume = 1,
      localSettingsNode,
      onVolumeChange,
      volumePercent = 100,
      volumeColor = '#22c55e',
      ...props
    }: VideoTrackProps,
    ref
  ) {
    const { effectiveTheme } = useTheme();
    const trackReference = useEnsureTrackRef(trackRef);
    const [zoomLevel, setZoomLevel] = React.useState(1);
    const [isHovered, setIsHovered] = React.useState(false);
    const [isVolumeHovered, setIsVolumeHovered] = React.useState(false);
    const [isPaused, setIsPaused] = React.useState(false);
    const [isVideoHidden, setIsVideoHidden] = React.useState(false);
    const [joinedDate, setJoinedDate] = React.useState(new Date());
    const [isWideEnough, setIsWideEnough] = useState(true);
    const [selectedQuality, setSelectedQuality] = React.useState<VideoQuality | VideoQuality.HIGH>(
      VideoQuality.HIGH
    );
    const [qualityMenuOpen, setQualityMenuOpen] = React.useState(false);
    const menuRef = React.useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const mediaEl = React.useRef<HTMLVideoElement>(null);
    React.useImperativeHandle(ref, () => mediaEl.current as HTMLVideoElement);

    const isMuted = volume === 0;

    // 👇 ДОБАВЛЕНО: цвета слайдера зависят от темы
    // const filledColor = effectiveTheme === 'dark' ? '#ffffff' : '#1f2937';
    // const unfilledColor = effectiveTheme === 'dark' ? '#4b5563' : '#d1d5db';
    const filledColor = effectiveTheme === 'dark' ? '#ffffff' : '#ffffff';
    const unfilledColor = effectiveTheme === 'dark' ? '#4b5563' : '#4b5563';

    const intersectionEntry = useHooks.useIntersectionObserver({
      root: mediaEl.current,
    });
    const [debouncedIntersectionEntry] = useHooks.useDebounceValue(intersectionEntry, 3000);
    const [isFullscreen, setIsFullscreen] = React.useState(false);
    const [isPip, setIsPip] = React.useState(false);
    const [intrinsicSize, setIntrinsicSize] = React.useState({ width: 0, height: 0 });
    const isLocal = trackReference.participant.isLocal;
    const isCamera = trackReference.participant.isCameraEnabled;
    const isScreenShare = trackReference.participant.isScreenShareEnabled;

    const togglePlayPause = () => {
      const video = mediaEl.current;
      if (!video) return;
      if (video.paused) {
        video.play().catch(console.error);
        setIsPaused(false);
      } else {
        video.pause();
        setIsPaused(true);
      }
    };

    React.useEffect(() => {
      const video = mediaEl.current;
      if (!video) return;
      const update = () => setIntrinsicSize({ width: video.videoWidth, height: video.videoHeight });
      update();
      video.addEventListener('loadedmetadata', update);
      video.addEventListener('resize', update);
      const interval = setInterval(update, 1000);
      return () => {
        video.removeEventListener('loadedmetadata', update);
        video.removeEventListener('resize', update);
        clearInterval(interval);
      };
    }, []);

    const qualityBadge = (() => {
      if (!intrinsicSize.height || (intrinsicSize.width === 2 && intrinsicSize.height === 2))
        return null;
      const height = intrinsicSize.height;
      if (height < 180) return '144p';
      if (height < 240) return '180p';
      if (height < 320) return '240p';
      if (height < 400) return '360p';
      if (height < 600) return '480p';
      if (height < 900) return '720p';
      if (height < 1200) return '1080p';
      if (height < 2200) return '1440p';
      return '2160p';
    })();

    React.useEffect(() => {
      if (
        manageSubscription &&
        trackReference.publication instanceof RemoteTrackPublication &&
        debouncedIntersectionEntry?.isIntersecting === false &&
        intersectionEntry?.isIntersecting === false
      ) {
        trackReference.publication.setSubscribed(false);
      }
    }, [debouncedIntersectionEntry, trackReference, manageSubscription]);

    React.useEffect(() => {
      if (
        manageSubscription &&
        trackReference.publication instanceof RemoteTrackPublication &&
        intersectionEntry?.isIntersecting === true
      ) {
        trackReference.publication.setSubscribed(true);
      }
    }, [intersectionEntry, trackReference, manageSubscription]);

    const {
      elementProps,
      publication: pub,
      isSubscribed,
    } = useMediaTrackBySourceOrName(trackReference, { element: mediaEl, props });

    React.useEffect(() => {
      onSubscriptionStatusChanged?.(!!isSubscribed);
      const joinedAt = trackReference.participant.joinedAt;
      if (!joinedAt) return;
      setJoinedDate(joinedAt);
    }, [isSubscribed, onSubscriptionStatusChanged]);

    React.useEffect(() => {
      if (!containerRef.current) return;
      const observer = new ResizeObserver(([entry]) =>
        setIsWideEnough(entry.contentRect.width > 240)
      );
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }, []);

    React.useEffect(() => {
      const video = mediaEl.current;
      if (!video) return;
      try {
        if (typeof document === 'undefined' || !document.pictureInPictureEnabled) return;
      } catch (err) {
        console.error(err);
      }
      const handleEnterPip = () => setIsPip(true);
      const handleLeavePip = () => setIsPip(false);
      video.addEventListener('enterpictureinpicture', handleEnterPip);
      video.addEventListener('leavepictureinpicture', handleLeavePip);
      return () => {
        video.removeEventListener('enterpictureinpicture', handleEnterPip);
        video.removeEventListener('leavepictureinpicture', handleLeavePip);
      };
    }, []);

    const clickHandler = (evt: React.MouseEvent<HTMLVideoElement, MouseEvent>) => {
      onClick?.(evt);
      onTrackClick?.({ participant: trackReference?.participant, track: pub });
    };

    const toggleFullscreen = () => {
      if (containerRef.current) {
        if (!isFullscreen) containerRef.current.requestFullscreen().catch(console.error);
        else if (document.fullscreenElement) document.exitFullscreen().catch(console.error);
        setIsFullscreen(!isFullscreen);
      }
    };

    React.useEffect(() => {
      const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
      document.addEventListener('fullscreenchange', handleFullscreenChange);
      return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    const togglePip = async () => {
      const video = mediaEl.current;
      if (!video) return;
      try {
        if (document.pictureInPictureElement === video) await document.exitPictureInPicture();
        else if (document.pictureInPictureEnabled) await video.requestPictureInPicture();
      } catch (err) {
        console.error('Failed to toggle Picture-in-Picture:', err);
      }
    };

    const handleDoubleClick = () => {
      if (focus) toggleFullscreen();
    };

    const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.1, 2));
    const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.1, 0.5));
    const handleResetZoom = () => setZoomLevel(1);

    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node))
          setQualityMenuOpen(false);
      };
      if (qualityMenuOpen) document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [qualityMenuOpen]);

    React.useEffect(() => {
      const pub = trackReference.publication;
      if (!(pub instanceof RemoteTrackPublication)) return;
      if (selectedQuality !== VideoQuality.HIGH) void pub.setVideoQuality(selectedQuality);
      else void pub.setVideoQuality(VideoQuality.HIGH);
    }, [trackReference, selectedQuality]);

    const isPipSupported = typeof document !== 'undefined' && document.pictureInPictureEnabled;

    return (
      <div
        ref={containerRef}
        className="group relative size-full"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onPointerEnter={() => setIsHovered(true)}
        onPointerLeave={() => setIsHovered(false)}
        onTouchStart={() => setIsHovered(true)}
        onTouchEnd={() => setIsHovered(false)}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            transform: `scale(${zoomLevel})`,
            transformOrigin: 'center center',
            transition: 'transform 0.3s ease',
          }}
          className="relative size-full"
        >
          {!isPardusBrowser() && (
            <div className="absolute inset-0 justify-center flex watermark">
              <div
                className="flex flex-row items-center gap-2 text-gray-500 text-xl font-bold transform rotate-[-5deg] select-none"
                style={{ opacity: '0.03' }}
              >
                <CyberKoalaCallsIcon className="w-12 h-12 grayscale" style={{ opacity: '0.5' }} />{' '}
                <span>CyberKoala® Calls</span>
              </div>
            </div>
          )}
          {isFullscreen && (
            <div className="absolute top-0 left-0 w-full h-[40px] bg-black/60 backdrop-blur-sm z-20 flex items-center justify-between px-2 pl-12 text-white select-none">
              <div className="text-sm font-medium">
                Видео демонстрирует: {trackReference?.participant?.identity.split('_')[0]}
              </div>
            </div>
          )}
          <div className={isFullscreen ? 'pt-[40px] h-full w-full' : 'h-full w-full'}>
            <video
              ref={mediaEl}
              {...elementProps}
              muted={false}
              onClick={clickHandler}
              onDoubleClick={handleDoubleClick}
              className="size-full rounded-md object-cover lg:object-contain"
              style={{
                display: isVideoHidden ? 'none' : 'block',
                borderRadius: '0.6em',
                padding: '0.3em',
                borderStyle: 'solid',
                borderWidth: '2px',
                borderColor: 'transparent',
              }}
            ></video>
            {/* 👇 ИЗМЕНЕНО: добавлено !isFullscreen, чтобы в фуллскрине бордер не рисовался */}
            {trackReference.source === Track.Source.ScreenShare &&
              screenShareAudioLevel > 0.05 &&
              !isFullscreen && (
                <div
                  style={{
                    borderWidth: `${Math.min(16, screenShareAudioLevel * 16)}px`,
                    borderColor: '#22c55e',
                    borderStyle: 'solid',
                    borderRadius: '0.6em',
                    transition: 'border-width 0.1s ease-out',
                    pointerEvents: 'none',
                  }}
                  className="absolute inset-0 box-border"
                />
              )}
          </div>
        </div>
        {isPipSupported && showControls && isHovered && isWideEnough && (
          <button
            onClick={togglePip}
            className={`absolute right-2 bottom-1/2 rounded-full p-2 text-white transition ${isPip ? 'bg-blue-600/80 hover:bg-blue-600' : 'bg-black/40 hover:bg-black/60'}`}
            title={isPip ? 'Exit Picture-in-Picture' : 'Enter Picture-in-Picture'}
          >
            <PictureInPicture size={16} />
          </button>
        )}
        {showControls && isHovered && isWideEnough && (isScreenShare) && (
          <div className="absolute bottom-0 left-0 right-0 z-50 flex items-center justify-between p-2 bg-gradient-to-t from-black/80 to-transparent pointer-events-none">
            <div className="flex items-center gap-2 pointer-events-auto">
              {!isLocal && (
                <>
                  <button
                    onClick={togglePlayPause}
                    className="rounded-full bg-black/40 p-2 text-white hover:bg-black/60 transition"
                    title={isPaused ? 'Play' : 'Pause'}
                  >
                    {isPaused ? <Play size={16} /> : <Pause size={16} />}
                  </button>
                  <div
                    className="flex items-center gap-1"
                    onMouseEnter={() => setIsVolumeHovered(true)}
                    onMouseLeave={() => setIsVolumeHovered(false)}
                  >
                    <button
                      onClick={onMuteParticipant}
                      className={`rounded-full p-2 transition shrink-0 ${isMuted ? 'bg-red-500/80 text-white hover:bg-red-500' : 'bg-black/40 text-white hover:bg-black/60'}`}
                      title={isMuted ? 'Unmute participant' : 'Mute participant'}
                    >
                      {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                    </button>

                    <div
                      className={cn(
                        'flex items-center gap-1 w-[140px] transition-opacity duration-200 ease-out',
                        isVolumeHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
                      )}
                    >
                      <span className="text-[12px] text-white/80 whitespace-nowrap min-w-[28px]">
                        {Math.round(volumePercent)}%
                      </span>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={volume}
                        onChange={(e) => {
                          const newVol = parseFloat(e.target.value);
                          onVolumeChange?.(newVol);
                        }}
                        style={{
                          width: '200px',
                          appearance: 'none',
                          height: '6px',
                          borderRadius: '4px',
                          outline: 'none',
                          cursor: 'pointer',
                          background: `linear-gradient(to right, ${filledColor} ${volumePercent}%, ${unfilledColor} ${volumePercent}%)`,
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
              {localSettingsNode && (
                <div className="rounded-full bg-black/40 p-2 text-white hover:bg-black/60 transition cursor-pointer flex items-center justify-center">
                  {localSettingsNode}
                </div>
              )}
            </div>

            {isWideEnough && (
              <div className="flex items-center gap-2 pointer-events-auto">
                <button
                  onClick={handleZoomOut}
                  className="rounded-full bg-black/40 p-2 text-white hover:bg-black/60 transition"
                  title="Zoom out"
                >
                  <ZoomOutIcon size={16} />
                </button>
                <button
                  onClick={handleResetZoom}
                  className="rounded-full bg-black/40 px-2 py-1 text-xs text-white hover:bg-black/60 transition min-w-[48px] text-center"
                  title="Reset zoom"
                >
                  {Math.round(zoomLevel * 100)}%
                </button>
                <button
                  onClick={handleZoomIn}
                  className="rounded-full bg-black/40 p-2 text-white hover:bg-black/60 transition"
                  title="Zoom in"
                >
                  <ZoomInIcon size={16} />
                </button>
              </div>
            )}

            {isWideEnough && (
              <div className="flex items-center gap-2 pointer-events-auto">
                {qualityBadge && (
                  <span
                    className="rounded-full bg-black/40 px-2 py-1 text-[10px] font-semibold text-white shadow-md backdrop-blur-sm border border-white/20"
                    title={`Stream quality: ${qualityBadge}`}
                  >
                    {qualityBadge}
                  </span>
                )}

                <div className="relative hidden">
                  <button
                    onClick={() => setQualityMenuOpen(!qualityMenuOpen)}
                    className="rounded-full bg-black/40 p-2 text-white shadow-md backdrop-blur-sm border border-white/20 hover:bg-black/60 transition"
                    title="Change video quality"
                  >
                    <Settings size={16} />
                  </button>
                  {qualityMenuOpen && (
                    <div
                      ref={menuRef}
                      className="absolute bottom-10 right-0 bg-black/90 backdrop-blur-md rounded-md shadow-lg border border-white/10 p-1 flex flex-col gap-0.5 min-w-[120px] z-30"
                    >
                      <div className="px-2 py-1 text-[10px] text-gray-400 font-semibold uppercase tracking-wider border-b border-white/10 mb-1">
                        Качество {qualityBadge && `(${qualityBadge})`}
                      </div>
                      <button
                        onClick={() => {
                          setSelectedQuality(VideoQuality.HIGH);
                          setQualityMenuOpen(false);
                        }}
                        className={`text-left px-2 py-1.5 text-xs rounded hover:bg-white/10 transition ${selectedQuality === VideoQuality.HIGH ? 'text-blue-400 bg-white/5' : 'text-white'}`}
                      >
                        Авто
                      </button>
                      <button
                        onClick={() => {
                          setSelectedQuality(VideoQuality.HIGH);
                          setQualityMenuOpen(false);
                        }}
                        className={`text-left px-2 py-1.5 text-xs rounded hover:bg-white/10 transition ${selectedQuality === VideoQuality.HIGH ? 'text-blue-400 bg-white/5' : 'text-white'}`}
                      >
                        Высокое
                      </button>
                      <button
                        onClick={() => {
                          setSelectedQuality(VideoQuality.MEDIUM);
                          setQualityMenuOpen(false);
                        }}
                        className={`text-left px-2 py-1.5 text-xs rounded hover:bg-white/10 transition ${selectedQuality === VideoQuality.MEDIUM ? 'text-blue-400 bg-white/5' : 'text-white'}`}
                      >
                        Среднее
                      </button>
                      <button
                        onClick={() => {
                          setSelectedQuality(VideoQuality.LOW);
                          setQualityMenuOpen(false);
                        }}
                        className={`text-left px-2 py-1.5 text-xs rounded hover:bg-white/10 transition ${selectedQuality === VideoQuality.LOW ? 'text-blue-400 bg-white/5' : 'text-white'}`}
                      >
                        Low
                      </button>
                    </div>
                  )}
                </div>

                {!isLocal && (
                  <button
                    onClick={toggleFullscreen}
                    className="rounded-full mr-4 bg-black/40 p-2 text-white hover:bg-black/60 transition"
                    title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                  >
                    {isFullscreen ? (
                      <MdFullscreenExit size={16} color={'#fff'} />
                    ) : (
                      <MdFullscreen size={16} color={'#fff'} />
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);
